import {
  AnalyticsComponent,
  AnalyticsFieldName,
} from "@/constants/analytics/Analytics";
import {
  ScheduleAnalyticsEvent,
  ScheduleAnalyticsSources,
} from "@/constants/analytics/Scheduling";
import {
  CTAText,
  EventCancellationError,
  EventCancellationText,
} from "@/constants/Schedule";
import { ConfirmationAlertTestID } from "@/constants/TestID";
import { useToast } from "@/hooks/useToast";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import { useDeclineGroupSectionEventMutation } from "@/store/api/schedule/groupClasses";
import ConfirmationAlert from "@/ui/composed/alerts/ConfirmationAlert";
import { FAIconName } from "@/ui/core";

interface DeclineGroupSectionEventConfirmationAlertProps {
  eventTitle: string;
  groupSectionEventId: string;
  isOpen: boolean;
  onCompletion: () => void;
  onDismiss: () => void;
  patientExternalId?: string;
}

// v2 analog of DeclineGroupEventConfirmationAlert: declines a single group section
// event occurrence via the v2 group-section-events endpoint.
const DeclineGroupSectionEventConfirmationAlert = ({
  eventTitle,
  groupSectionEventId,
  isOpen,
  onCompletion,
  onDismiss,
  patientExternalId,
}: DeclineGroupSectionEventConfirmationAlertProps) => {
  const { captureException } = useAppMonitor();
  const { track } = useAnalytics();
  const { showToast } = useToast();
  const [declineGroupSectionEvent] = useDeclineGroupSectionEventMutation();

  const handleDeclineEventConfirm = () => {
    track(ScheduleAnalyticsEvent.GroupEventDetailsDecline, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Button,
      [AnalyticsFieldName.EventId]: groupSectionEventId,
      [AnalyticsFieldName.EventTitle]: eventTitle,
      [AnalyticsFieldName.Source]:
        ScheduleAnalyticsSources.GroupEventDetailView,
    });

    declineGroupSectionEvent({ groupSectionEventId, patientExternalId })
      .unwrap()
      .then(() => onCompletion())
      .catch((error) => {
        captureException(
          new Error(EventCancellationError.DecliningGroupClassEventError, {
            cause: error,
          }),
        );
        showToast({
          text: EventCancellationError.DecliningGroupClassEventError,
          variant: "error",
        });
        onDismiss();
      });
  };

  return (
    <ConfirmationAlert
      confirmationType="destructive"
      confirmLeftIcon={FAIconName.Trash}
      confirmText={CTAText.DeclineEvent}
      isConfirmFullWidth
      isOpen={isOpen}
      message={EventCancellationText.DeclineGroupClassEventAlertMessage}
      onConfirm={handleDeclineEventConfirm}
      onDismiss={onDismiss}
      testID={ConfirmationAlertTestID.DeclineGroup}
      title={EventCancellationText.DeclineGroupClassEventAlertTitle}
    />
  );
};

export default DeclineGroupSectionEventConfirmationAlert;
